import { useCurrentUserId } from '@framework/auth';
import { trpcClient } from '@framework/trpcClient';
import { Button } from '@framework/ui/atoms/Button';
import { Loading } from '@framework/ui/atoms/Loading';
import { useGroupMemberCount } from '@group/hooks/useGroupMemberCount';
import { type GroupMember, useGroupMembers } from '@group/hooks/useGroupMembers';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { STANDARD_LICENSE_MONTHLY_FEE, CARD_SUBSCRIPTION_TAX_RATE } from './licensePricing';
import type { GroupId, GroupLicenseType, UserId } from 'balus-common';

type CheckoutResult = 'success' | 'canceled' | null;

type StartCheckoutInput = {
  groupId: GroupId;
  billingManagerUserId: UserId;
  seatCount: number;
};

type CheckoutAdmin = Pick<GroupMember, 'id' | 'name' | 'mailAddress'>;

type FormProps = {
  isTrial: boolean;
  groupId: GroupId;
  admins: CheckoutAdmin[];
  currentUserId: UserId | null;
  minimumSeatCount: number;
  isSeatCountUnknown?: boolean;
  startCheckout: (input: StartCheckoutInput) => Promise<{ url: string }>;
  redirectToCheckout: (url: string) => void;
};

export const getCheckoutAdmins = (members: GroupMember[] | null): CheckoutAdmin[] =>
  members?.filter(({ role }) => role === 'admin').map(({ id, name, mailAddress }) => ({ id, name, mailAddress })) ?? [];

export const getMinimumSeatCount = (counts: { member: number; guest: number } | undefined): number | null =>
  counts ? Math.max(1, counts.member, counts.guest) : null;

export const getCheckoutResult = (value: string | null): CheckoutResult =>
  value === 'success' || value === 'canceled' ? value : null;

export const shouldShowCardSubscriptionCheckoutForm = (
  licenseType: GroupLicenseType | undefined,
  isEnabled: boolean,
  checkoutResult: CheckoutResult
): boolean => (licenseType === 'free' || licenseType === 'trial') && isEnabled && checkoutResult !== 'success';

type ResultNoticeProps = {
  checkoutResult: Exclude<CheckoutResult, null>;
};

export const CardSubscriptionCheckoutResultNotice: React.FC<ResultNoticeProps> = ({
  checkoutResult,
}: ResultNoticeProps) => {
  const { t } = useTranslation();
  const isSuccess = checkoutResult === 'success';

  return (
    <section
      className={`mt-8 border-l-4 p-5 sm:px-6 ${isSuccess ? 'border-green-600 bg-green-50' : 'border-gray-400 bg-gray-50'}`}
      aria-live="polite"
    >
      <h2 className={`font-bold ${isSuccess ? 'text-green-800' : 'text-gray-800'}`}>
        {t(
          isSuccess ? 'group.cardSubscriptionCheckout.success.title' : 'group.cardSubscriptionCheckout.canceled.title'
        )}
      </h2>
      <p className="mt-1 text-sm text-gray-700">
        {t(
          isSuccess
            ? 'group.cardSubscriptionCheckout.success.message'
            : 'group.cardSubscriptionCheckout.canceled.message'
        )}
      </p>
    </section>
  );
};

export const CardSubscriptionCheckoutForm: React.FC<FormProps> = ({
  groupId,
  admins,
  currentUserId,
  minimumSeatCount,
  isSeatCountUnknown = false,
  isTrial,
  startCheckout,
  redirectToCheckout,
}: FormProps) => {
  const { t } = useTranslation();
  const [billingManagerUserId, setBillingManagerUserId] = useState<UserId | ''>(
    () => (admins.find(({ id }) => id === currentUserId) ?? admins[0])?.id ?? ''
  );
  const [seatCount, setSeatCount] = useState(() => (isSeatCountUnknown ? '' : String(minimumSeatCount)));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (admins.some(({ id }) => id === billingManagerUserId)) return;

    const initialAdmin = admins.find(({ id }) => id === currentUserId) ?? admins[0];
    setBillingManagerUserId(initialAdmin?.id ?? '');
  }, [admins, billingManagerUserId, currentUserId]);

  useEffect(() => {
    if (isSeatCountUnknown) return;

    setSeatCount((current) => {
      const currentNumber = Number(current);
      return Number.isInteger(currentNumber) && currentNumber >= minimumSeatCount ? current : String(minimumSeatCount);
    });
  }, [minimumSeatCount, isSeatCountUnknown]);

  const parsedSeatCount = Number(seatCount);
  const monthlyFeeIncludingTax = Math.round(STANDARD_LICENSE_MONTHLY_FEE * (1 + CARD_SUBSCRIPTION_TAX_RATE));
  const monthlyTotal = parsedSeatCount * monthlyFeeIncludingTax;
  const canShowMonthlyTotal =
    Number.isSafeInteger(parsedSeatCount) && parsedSeatCount >= minimumSeatCount && Number.isSafeInteger(monthlyTotal);
  const hasSelectedAdmin = admins.some(({ id }) => id === billingManagerUserId);
  const canSubmit = hasSelectedAdmin && canShowMonthlyTotal && !isSubmitting;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || billingManagerUserId === '') return;

    setHasError(false);
    setIsSubmitting(true);
    try {
      const { url } = await startCheckout({ groupId, billingManagerUserId, seatCount: parsedSeatCount });
      redirectToCheckout(url);
    } catch {
      setHasError(true);
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-4" aria-label={t('group.cardSubscriptionCheckout.title')}>
      <div className="max-w-3xl">
        <p className="mt-1 text-sm leading-6 text-gray-600">{t('group.cardSubscriptionCheckout.description')}</p>

        {isTrial && (
          <p className="mt-3 text-sm font-bold text-gray-800">{t('group.cardSubscriptionCheckout.trial.message')}</p>
        )}

        <div className="mt-4 space-y-2 text-sm leading-6 text-gray-700">
          <p>
            {t('group.cardSubscriptionCheckout.terms.price.message', {
              excludingTax: STANDARD_LICENSE_MONTHLY_FEE.toLocaleString('ja-JP'),
              includingTax: monthlyFeeIncludingTax.toLocaleString('ja-JP'),
            })}
          </p>
          <p>{t('group.cardSubscriptionCheckout.terms.initial.message')}</p>
          <p>{t('group.cardSubscriptionCheckout.terms.renewal.message')}</p>
          <p>{t('group.cardSubscriptionCheckout.terms.licenses.message')}</p>
          <p>
            <Trans
              i18nKey="group.cardSubscriptionCheckout.terms.cancellation.message"
              components={{
                contactLink: (
                  <a
                    href="https://balus-help.levii.co.jp/hc/ja/requests/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline hover:no-underline"
                  />
                ),
              }}
            />
          </p>
          <p>
            <Trans
              i18nKey="group.cardSubscriptionCheckout.terms.legal.message"
              components={{
                termsLink: (
                  <a
                    href="https://levii.co.jp/legal/terms-of-service/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline hover:no-underline"
                  />
                ),
                tradeLawLink: (
                  <a
                    href="https://levii.co.jp/legal/tradelaw/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline hover:no-underline"
                  />
                ),
              }}
            />
          </p>
        </div>

        <form className="mt-5" onSubmit={handleSubmit}>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="billing-manager" className="block text-sm font-bold text-gray-800">
                {t('group.cardSubscriptionCheckout.billingManager.label')}
              </label>
              <select
                id="billing-manager"
                className="form-select mt-2 w-full rounded border-gray-400 bg-white text-sm"
                value={billingManagerUserId}
                onChange={(event) => setBillingManagerUserId(event.currentTarget.value as UserId)}
                disabled={isSubmitting || admins.length === 0}
              >
                {admins.length === 0 && (
                  <option value="">{t('group.cardSubscriptionCheckout.billingManager.empty')}</option>
                )}
                {admins.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.name} ({admin.mailAddress})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs leading-5 text-gray-600">
                {t('group.cardSubscriptionCheckout.billingManager.message')}
              </p>
            </div>

            <div>
              <label htmlFor="card-subscription-seat-count" className="block text-sm font-bold text-gray-800">
                {t('group.cardSubscriptionCheckout.seatCount.label')}
              </label>
              <input
                id="card-subscription-seat-count"
                className="form-input mt-2 w-full rounded border-gray-400 bg-white text-sm"
                type="number"
                min={minimumSeatCount}
                step={1}
                value={seatCount}
                onChange={(event) => setSeatCount(event.currentTarget.value)}
                onBlur={() => {
                  if (isSeatCountUnknown) return;

                  setSeatCount((current) => {
                    const currentNumber = Number(current);
                    return Number.isInteger(currentNumber) && currentNumber >= minimumSeatCount
                      ? current
                      : String(minimumSeatCount);
                  });
                }}
                disabled={isSubmitting}
              />
              <p className="mt-1 text-xs leading-5 text-gray-600">
                {isSeatCountUnknown
                  ? t('group.cardSubscriptionCheckout.seatCount.fetchFailed')
                  : t('group.cardSubscriptionCheckout.seatCount.minimum', { count: minimumSeatCount })}
              </p>
            </div>
          </div>

          {canShowMonthlyTotal && (
            <p className="mt-4 font-bold" aria-live="polite">
              {t('group.cardSubscriptionCheckout.terms.total.message', {
                amount: monthlyTotal.toLocaleString('ja-JP'),
              })}
            </p>
          )}

          {hasError && (
            <p className="mt-4 text-sm font-bold text-red-700" role="alert">
              {t('group.cardSubscriptionCheckout.error.message')}
            </p>
          )}

          <div className="mt-5 flex flex-col items-start gap-2 sm:flex-row sm:items-center">
            <Button type="submit" color="brand" disabled={!canSubmit} loading={isSubmitting}>
              {t('group.cardSubscriptionCheckout.submit.label')}
            </Button>
            <p className="text-xs leading-5 text-gray-600">{t('group.cardSubscriptionCheckout.submit.message')}</p>
          </div>
        </form>
      </div>
    </section>
  );
};

type Props = {
  groupId: GroupId;
  isTrial: boolean;
};

export const CardSubscriptionCheckoutPanel: React.FC<Props> = ({ groupId, isTrial }: Props) => {
  const { t } = useTranslation();
  const { members, failed: membersFailed } = useGroupMembers(groupId);
  const { counts: memberCount, failed: countsFailed } = useGroupMemberCount(groupId);
  const currentUserId = useCurrentUserId();
  const admins = useMemo(() => getCheckoutAdmins(members), [members]);
  const minimumSeatCount = getMinimumSeatCount(memberCount);

  if (members === null && membersFailed) {
    return (
      <p role="alert" className="py-5 text-sm text-red-700">
        {t('group.cardSubscriptionCheckout.billingManager.fetchFailed')}
      </p>
    );
  }

  if (members === null || (minimumSeatCount === null && !countsFailed)) {
    return (
      <div role="status" className="py-5">
        <Loading message={t('group.cardSubscriptionCheckout.seatCount.loading')} />
      </div>
    );
  }

  return (
    <CardSubscriptionCheckoutForm
      groupId={groupId}
      isTrial={isTrial}
      admins={admins}
      currentUserId={currentUserId}
      minimumSeatCount={minimumSeatCount ?? 1}
      isSeatCountUnknown={minimumSeatCount === null}
      startCheckout={(input) => trpcClient.group.startCardSubscriptionCheckout.mutate(input)}
      redirectToCheckout={(url) => window.location.assign(url)}
    />
  );
};
